/**
 * Sign-up gate state hook
 *
 * Pre-auth: invite callable resolution only. Post-auth: waits for the Firestore patient listener.
 */

import { useSyncExternalStore } from 'react';
import type { SignUpGateErrorReason } from '../config/onboardingFlowErrorCopy';
import {
  getSignUpId,
  getSignedUpPatientStatus,
  useOnboardingStore,
} from '../store/onboardingStore';
import { useOnboardingFirebaseAuthReady } from './useOnboardingFirebaseAuthReady';

export type OnboardingSignUpGateState =
  | { status: 'hydrating' }
  | { status: 'loading_patient' }
  | { status: 'ready' }
  | { status: 'error'; reason: SignUpGateErrorReason };

function subscribeOnboardingPersistHydration(onStoreChange: () => void) {
  return useOnboardingStore.persist.onFinishHydration(onStoreChange);
}

function getOnboardingPersistHydratedSnapshot() {
  return useOnboardingStore.persist.hasHydrated();
}

/**
 * Returns gate status for missing id, invite/patient loading, or ready to show routes.
 *
 * @return {Object} Discriminated gate state for the sign-up layout.
 */
export function useOnboardingSignUpGate(): OnboardingSignUpGateState {
  const persistHydrated = useSyncExternalStore(
    subscribeOnboardingPersistHydration,
    getOnboardingPersistHydratedSnapshot,
    () => false,
  );
  const signUpId = useOnboardingStore(getSignUpId);
  const inviteContextStatus = useOnboardingStore((s) => s.inviteContextStatus);
  const inviteContextResolvedForSignUpId = useOnboardingStore((s) => s.inviteContextResolvedForSignUpId);
  const signedUpPatientStatus = useOnboardingStore(getSignedUpPatientStatus);
  const firebaseAuthReady = useOnboardingFirebaseAuthReady();

  if (!persistHydrated) {
    return { status: 'hydrating' };
  }

  if (!signUpId) {
    return { status: 'error', reason: 'missing_sign_up_id' };
  }

  if (firebaseAuthReady === null) {
    return { status: 'loading_patient' };
  }

  if (!firebaseAuthReady) {
    if (inviteContextResolvedForSignUpId !== signUpId) {
      return { status: 'loading_patient' };
    }
    if (inviteContextStatus === 'idle' || inviteContextStatus === 'loading') {
      return { status: 'loading_patient' };
    }
    if (inviteContextStatus === 'not_found') {
      return { status: 'error', reason: 'patient_not_found' };
    }
    if (inviteContextStatus === 'error') {
      return { status: 'error', reason: 'patient_load_error' };
    }
    return { status: 'ready' };
  }

  if (signedUpPatientStatus === 'idle' || signedUpPatientStatus === 'loading') {
    return { status: 'loading_patient' };
  }

  if (signedUpPatientStatus === 'not_found') {
    return { status: 'error', reason: 'patient_not_found' };
  }

  if (signedUpPatientStatus === 'error') {
    return { status: 'error', reason: 'patient_load_error' };
  }

  return { status: 'ready' };
}
